import { useApp } from "../context/AppContext";
import { useLiveBill } from "../hooks/useLiveBill";
import { CONTROLLER_COUNTS, controllerLabel, controllerRate, groupTabLines, resolveRateAtInstant } from "@/lib/billing";
import LiveTimer from "./LiveTimer";
import AddToTabDialog from "./AddToTabDialog";
import BillPayment from "./BillPayment";
import TableStretches from "./TableStretches";
import { WaitingPickDialog } from "./WaitingTabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../components/ui/alert-dialog";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, Play, Square, Clock, IndianRupee, Pause, Plus, Minus, ShoppingBag, Pencil, Gamepad2, Trophy, Users, X } from "lucide-react";
import { Input } from "../components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formatDuration, formatMoney } from "../lib/format";
import { billShares, duesToPost, paymentProblem } from "../lib/dues";
import { currentFrameCharge, frameTally, isFrameGame } from "../lib/frames";

const TABLE_TYPE_LABELS = {
  snooker: "Snooker",
  american_pool: "American Pool",
  pool: "Normal Pool",
  playstation: "PlayStation",
};

function formatTime(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

// table_rates / playstation_rates are keyed by the table name; fall back to it
// when pricing_key is missing (older tables were stored without it). Mirrors the
// backend resolve_price_for_table.
function hourlyRate(table, settings, controllers) {
  const rateKey = table.pricing_key || table.name;
  if (table.table_type === "playstation") {
    // A count with no rate of its own (1 controller, before one is set)
    // costs the 2-controller rate — same rule as the backend.
    const psRates = settings?.playstation_rates?.[rateKey];
    if (psRates && typeof psRates === "object") {
      return controllerRate(psRates, controllers) ?? 120;
    }
    const legacyRates = settings?.playstation_rates;
    if (legacyRates && typeof legacyRates === "object" && controllerRate(legacyRates, controllers) != null) {
      return controllerRate(legacyRates, controllers);
    }
    return 120;
  }
  if (settings?.table_rates?.[rateKey] != null) {
    return settings.table_rates[rateKey];
  }
  if (table.table_type === "snooker") return settings.snooker_price_per_hour;
  return settings.pool_price_per_hour;
}

// The offer a game on this table would be charged right now, or null at the
// normal rate.
function offerNow(table, settings, controllers) {
  const rateKey = table.pricing_key || table.name;
  const windows = Array.isArray(settings?.pricing_windows) ? settings.pricing_windows : [];
  const enabled = windows.filter((w) => w.enabled);
  if (enabled.length === 0) return null;
  const plan = {
    base_rate: hourlyRate(table, settings, controllers),
    windows: enabled
      .map((w) => ({
        label: w.label || w.id,
        start: w.start,
        end: w.end,
        priority: w.priority,
        days: w.days,
        rate: table.table_type === "playstation"
          ? controllerRate(w.playstation_rates?.[rateKey], controllers)
          : w.table_rates?.[rateKey],
      }))
      .filter((w) => Number(w.rate) > 0),
  };
  const { rate, label } = resolveRateAtInstant(plan, Date.now());
  return label === "Normal" ? null : { rate, label };
}

export default function TableCard({ table }) {
  const {
    tables, fetchTables, startSession, stopSession, cancelSession, settings, pauseSession, resumeSession,
    setControllers, moveGame, recordFrame, removeFrame, removeFromTab, setTabQuantity, moveTabItem,
    renameTable, waiting, fetchWaiting,
  } = useApp();
  const { isAdmin } = useAuth();
  const [showBill, setShowBill] = useState(false);
  const [payChoices, setPayChoices] = useState({});
  const [showAddItems, setShowAddItems] = useState(false);
  // Which side's items are being added, when the picker is opened from a row
  const [addItemsFor, setAddItemsFor] = useState(null);
  // A tab line being moved onto someone's name
  const [movingLine, setMovingLine] = useState(null);
  // The frame-by-frame log, shown on request rather than filling the card
  const [showFrames, setShowFrames] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  // Renaming happens here, on the table itself, rather than in Settings
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [controllerCount, setControllerCount] = useState(table?.controller_count ?? 2);
  const [switchingControllers, setSwitchingControllers] = useState(false);
  // Loser pays: the group is named before the game starts, then each frame is
  // logged against whoever lost it.
  const [loserPays, setLoserPays] = useState(false);
  const [teamPlay, setTeamPlay] = useState(false);
  const [playerNames, setPlayerNames] = useState(["", ""]);
  // 2 v 2: two sides, each with its own names
  const [teamNames, setTeamNames] = useState([["", ""], ["", ""]]);
  const [frameBusy, setFrameBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [movingTo, setMovingTo] = useState(null);
  // Who this game might be for, while Start Game is asking.
  const [waitingChoices, setWaitingChoices] = useState([]);

  // A game that ended on three pads leaves three pads at the console, so the
  // next start offers that count rather than whatever was picked last time.
  useEffect(() => {
    if (table.status !== "occupied") setControllerCount(table.controller_count ?? 2);
  }, [table.status, table.controller_count]);

  const isOccupied = table.status === "occupied";
  const isSnooker = table.table_type === "snooker";
  const isPlaystation = table.table_type === "playstation";
  const session = table.active_session;
  // What the running game is being played on right now; older sessions (before
  // the count was recorded on the session) fall back to the console's.
  const liveControllers = session?.controller_count ?? table.controller_count ?? 2;
  const moves = session?.table_moves || [];
  const lastMove = moves[moves.length - 1];


  // The table's tab: items bought during this game, paid together on stop.
  // Shown grouped by item; "−" takes one unit off the most recent entry for
  // that item, whether that entry was a single tap or a bulk add of five.
  const framesGame = isFrameGame(session);
  const tally = framesGame ? frameTally(session) : null;
  // What the frame they are playing right now has run up. The live-bill hook
  // re-renders this card every second, so this follows the clock with it.
  const thisFrame = framesGame ? currentFrameCharge(session, Date.now()) : null;

  // A game that moved here starts a fresh tab on this table; what was bought
  // before the move waits on the bill, shown with the table it was bought at.
  const orders = session?.orders || [];
  const tab = lastMove
    ? orders.filter((o) => new Date(o.created_at) >= new Date(lastMove.at))
    : orders;
  const itemsTotal = orders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
  const tabTotal = tab.reduce((sum, o) => sum + (o.total_amount || 0), 0);
  const tabLines = groupTabLines(tab);
  // On a discard, what came off the waiting list goes back there, unpaid; only
  // the rest stays as cafe sales.
  const keptOrders = orders.filter((o) => !o.waiting_name);
  const keptLines = groupTabLines(keptOrders);
  const keptTotal = keptOrders.reduce((sum, o) => sum + o.total_amount, 0);
  const waitingNames = [...new Set(orders.map((o) => o.waiting_name).filter(Boolean))].join(", ");

  const tableRate = hourlyRate(table, settings, controllerCount);

  // Uses the pricing plan snapshotted on the session, so the live amount splits
  // across Happy Hour boundaries exactly like the backend's final bill.
  // Paused time is excluded, so the clock and the game bill both freeze on pause.
  const {
    amount: liveBill, rate: liveRate, label: liveLabel, segments: liveSegments,
    stretches, playedMs, billedMinutes, paused: isPaused,
  } = useLiveBill(session);
  const isOfferRateNow = !!session && liveLabel !== "Normal";
  // Frames replace the clock for a loser-pays game; everything downstream
  // (the card, the bill, the discard warning) reads this one number.
  const gameAmount = framesGame ? tally.framesTotal + thisFrame.amount : liveBill;
  const amountDue = gameAmount + itemsTotal;
  const shares = billShares(amountDue, framesGame ? tally : null);
  const payingLater = duesToPost(shares, payChoices).length > 0;
  const paymentBlocked = paymentProblem(shares, payChoices);
  // The table the game is on now. After a move the card's clock and amount are
  // this table's alone, and the earlier tables are brought forward as one line.
  const thisTable = stretches[stretches.length - 1] || { amount: liveBill, playedMs, segments: liveSegments };
  const cardAmount = moves.length > 0 ? thisTable.amount : gameAmount;
  const carriedFrom = [...new Set(stretches.slice(0, -1).map((s) => s.tableName))].join(", ");
  const carriedAmount = liveBill - thisTable.amount + itemsTotal - tabTotal;
  const pausedMinutes = session
    ? Math.max(0, (Date.now() - new Date(session.start_time).getTime() - playedMs) / 60000)
    : 0;

  // For an available table: the rate that would apply if started right now.
  const activeWindowNow = offerNow(table, settings, controllerCount);

  // Players can go anywhere that's free: snooker to pool, a table to a PlayStation.
  const moveTargets = tables.filter((t) => t.id !== table.id && t.status === "available");

  // The list isn't polled, so ask the server: someone may have been added on
  // another device. Nobody waiting with anything to pay means no question at all.
  const handleStart = async () => {
    setActionLoading(true);
    const latest = (await fetchWaiting()) ?? waiting;
    const owing = latest.filter((t) => t.orders.length > 0);
    if (owing.length > 0) {
      setWaitingChoices(owing);
      setActionLoading(false);
      return;
    }
    await startGame();
  };

  const startGame = async (waitingTab = null) => {
    setWaitingChoices([]);
    setActionLoading(true);
    try {
      let payload = {};
      if (table.table_type === "playstation") {
        payload = { controller_count: controllerCount };
      } else if (isSnooker && loserPays) {
        payload = teamPlay
          ? {
              billing_mode: "frames",
              teams: teamNames.map((names, i) => ({
                name: `Team ${String.fromCharCode(65 + i)}`,
                players: names.map((n) => n.trim()).filter(Boolean),
              })),
            }
          : {
              billing_mode: "frames",
              players: playerNames.map((n) => n.trim()).filter(Boolean),
            };
      }
      if (waitingTab) payload.waiting_tab_id = waitingTab.id;
      await startSession(table.id, payload);
      toast.success(
        waitingTab
          ? `${table.name} started — ${waitingTab.name}'s items are on the bill`
          : `${table.name} started!`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start session");
    }
    setActionLoading(false);
  };

  // Open the confirmation dialog WITHOUT stopping. The game keeps running until the
  // user confirms payment, so an accidental "Stop Game" tap can be safely cancelled.
  const openStopConfirm = () => {
    setPayChoices({});
    setShowBill(true);
  };

  const handleConfirmStop = async () => {
    setActionLoading(true);
    try {
      const result = await stopSession(table.id, duesToPost(shares, payChoices));
      setShowBill(false);
      // Both figures are the server's (game + tab) — the ones actually recorded.
      const owed = result.dues || [];
      toast.success(owed.length > 0
        ? `On dues: ${owed.map((d) => `₹${formatMoney(d.amount)} ${d.name}`).join(", ")}`
        : `Payment recorded: ₹${formatMoney(result.grand_total ?? result.total_amount)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to stop session");
    }
    setActionLoading(false);
  };

  // Discards the game outright: no bill, nothing in history or the day's takings.
  const handleCancelGame = async () => {
    setCancelLoading(true);
    try {
      const res = await cancelSession(table.id);
      setShowCancel(false);
      const kept = res?.items_kept || 0;
      const returned = res?.items_returned || 0;
      const outcome = [
        returned > 0 && `${returned} item${returned > 1 ? "s" : ""} back on the waiting list`,
        kept > 0 && `${kept} item${kept > 1 ? "s" : ""} kept as cafe sales`,
      ].filter(Boolean).join(", ");
      toast.success(`${table.name} discarded — ${outcome || "nothing recorded"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't discard the game");
    }
    setCancelLoading(false);
  };

  // Tables aren't polled, so re-read them: another device may have started a
  // game on one that still looks free here.
  const openMove = () => {
    setShowMove(true);
    fetchTables();
  };

  // The players change tables and the game goes with them. This table's time and
  // items stay on the bill; the new table starts fresh at its own rate.
  const handleMove = async (target) => {
    // showMove: a double tap can land while the dialog is still fading out
    if (movingTo || !showMove) return;
    setMovingTo(target.id);
    try {
      await moveGame(table.id, target.id);
      setShowMove(false);
      toast.success(`Moved to ${target.name} — ${table.name}'s bill is carried over`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move the game");
    }
    setMovingTo(null);
  };

  const handleRename = async () => {
    const name = renameValue.trim();
    if (!name) { toast.error("Enter a name"); return; }
    if (name === table.name) { setRenaming(false); return; }
    setSavingRename(true);
    try {
      await renameTable(table.id, name);
      toast.success(`Renamed to ${name}`);
      setRenaming(false);
    } catch (e) {
      // e.g. another table already uses that name
      toast.error(e?.response?.data?.detail || "Failed to rename");
    }
    setSavingRename(false);
  };

  const handleTogglePause = async () => {
    if (pauseLoading) return;
    setPauseLoading(true);
    try {
      if (isPaused) {
        await resumeSession(table.id);
        toast.success(`${table.name} resumed`);
      } else {
        await pauseSession(table.id);
        toast(`${table.name} paused — clock and bill stopped`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the game");
    }
    setPauseLoading(false);
  };

  // Players join or leave mid-game. The clock never stops: what's already been
  // played keeps its rate, and the new rate starts from this moment.
  const handleSetControllers = async (count) => {
    if (switchingControllers || Number(count) === Number(liveControllers)) return;
    setSwitchingControllers(true);
    try {
      await setControllers(table.id, Number(count));
      toast.success(`${controllerLabel(count)} from now — time already played keeps its rate`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change the controllers");
    }
    setSwitchingControllers(false);
  };

  const openItemsFor = (side) => {
    setAddItemsFor(side || null);
    setShowAddItems(true);
  };

  // Moves every entry on one tab line onto a name — for a drink that went on
  // the table before anyone said whose it was.
  const handleMoveLine = async (line, moveTo) => {
    setMovingLine(null);
    try {
      for (const entry of line.entries) {
        await moveTabItem(table.id, entry.id, moveTo);
      }
      const who = moveTo === "table"
        ? "the table"
        : (session?.players || []).find((p) => p.id === moveTo)?.name || "that player";
      toast.success(`${line.name} moved to ${who}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move it");
    }
  };

  const handleFrameLost = async (side) => {
    if (frameBusy) return;
    setFrameBusy(true);
    try {
      const amount = thisFrame?.amount || 0;
      await recordFrame(table.id, side.sideId);
      toast.success(`Frame to ${side.name} — ₹${formatMoney(amount)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record the frame");
    }
    setFrameBusy(false);
  };

  const handleRemoveFrame = async (frame) => {
    if (!frame || frameBusy) return;
    setFrameBusy(true);
    try {
      await removeFrame(table.id, frame.id);
      toast(`Frame removed (${frame.loser_name})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove the frame");
    }
    setFrameBusy(false);
  };

  const handleRemoveOne = async (line) => {
    const last = line.entries[line.entries.length - 1];
    setRemovingId(line.itemId);
    try {
      if ((last.quantity || 1) > 1) {
        // A bulk line: drop its count by one rather than wiping the lot
        await setTabQuantity(table.id, last.id, last.quantity - 1);
      } else {
        await removeFromTab(table.id, last.id);
      }
      toast(`Removed one ${line.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove the item");
    }
    setRemovingId(null);
  };

  const handleCancelStop = () => {
    if (actionLoading) return; // don't cancel while a stop is being confirmed
    setShowBill(false);
    toast("Game continues");
  };

  const accentColor = isSnooker ? "green" : "blue";
  const glowClass = isOccupied ? (isSnooker ? "glow-green" : "glow-blue") : "";

  return (
    <>
      <div
        data-testid={`table-card-${table.id}`}
        className={`
          relative rounded-xl border p-6 transition-all duration-300
          ${isOccupied
            ? `bg-[#111113] border-${accentColor}-500/40 ${glowClass}`
            : "bg-[#18181b] border-zinc-800 hover:border-zinc-600"
          }
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0 flex-1">
            {renaming ? (
              <div className="flex items-center gap-2">
                <Input
                  data-testid={`rename-input-${table.id}`}
                  autoFocus
                  value={renameValue}
                  maxLength={40}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRename();
                    if (e.key === "Escape") setRenaming(false);
                  }}
                  className="h-9 bg-[#09090b] border-zinc-700 text-zinc-100 font-heading text-lg"
                />
                <Button
                  data-testid={`rename-save-${table.id}`}
                  onClick={handleRename}
                  disabled={savingRename}
                  className="h-9 bg-green-600 hover:bg-green-700 text-white shrink-0 px-3"
                >
                  {savingRename ? "..." : "Save"}
                </Button>
                <Button
                  variant="outline"
                  data-testid={`rename-cancel-${table.id}`}
                  onClick={() => setRenaming(false)}
                  disabled={savingRename}
                  className="h-9 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 shrink-0 px-3"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-2xl font-bold tracking-tight text-zinc-100 truncate">
                  {table.name}
                </h3>
                {isAdmin && (
                  <button
                    type="button"
                    aria-label={`Rename ${table.name}`}
                    data-testid={`rename-btn-${table.id}`}
                    onClick={() => { setRenameValue(table.name); setRenaming(true); }}
                    className="text-zinc-600 hover:text-green-400 transition-colors duration-150 shrink-0"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            <span
              className={`inline-block mt-1 text-xs font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                isSnooker
                  ? "bg-green-500/15 text-green-400"
                  : table.table_type === "american_pool"
                  ? "bg-teal-500/15 text-teal-400"
                  : "bg-blue-500/15 text-blue-400"
              }`}
            >
              {TABLE_TYPE_LABELS[table.table_type] || table.table_type}
            </span>
          </div>
          <div
            className={`w-3 h-3 rounded-full ${
              !isOccupied ? "bg-green-500" : isPaused ? "bg-amber-400" : "bg-red-500 animate-pulse"
            }`}
            data-testid={`table-status-indicator-${table.id}`}
          />
        </div>

        {/* Body */}
        {isOccupied && session ? (
          <div className="space-y-4">
            {/* Timer — play time only; frozen while paused */}
            <div className="text-center py-3">
              <LiveTimer playedMs={thisTable.playedMs} paused={isPaused} />
              {isPaused && (
                <div className="mt-1">
                  <span
                    className="text-[10px] font-semibold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400"
                    data-testid={`paused-badge-${table.id}`}
                  >
                    Paused{pausedMinutes >= 1 ? ` ${formatDuration(pausedMinutes)}` : ""} · not charging
                  </span>
                </div>
              )}
            </div>

            {/* Info Row */}
            <div className="flex items-center justify-between text-sm text-zinc-400">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>Started {formatTime(lastMove?.at ?? session.start_time)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-zinc-100 font-semibold" title="Game amount">
                <IndianRupee className="w-3.5 h-3.5" />
                <span className="font-mono-timer text-lg" data-testid={`live-bill-${table.id}`}>
                  {formatMoney(cardAmount)}
                </span>
              </div>
            </div>

            {/* Still owed from the table they came from, itemised on the Stop bill */}
            {carriedFrom && (
              <div
                className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs"
                data-testid={`carried-${table.id}`}
              >
                <span className="flex items-center gap-1.5 min-w-0 text-zinc-300">
                  <ArrowRightLeft className="w-3.5 h-3.5 shrink-0 text-amber-400" />
                  <span>Brought from {carriedFrom}</span>
                </span>
                <span className="shrink-0 font-semibold text-amber-300">₹{formatMoney(carriedAmount)}</span>
              </div>
            )}

            {/* Current pricing window — the bill splits automatically when it changes */}
            {!framesGame && (
            <div className="flex items-center justify-center">
              {isOfferRateNow ? (
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400"
                  data-testid={`live-rate-label-${table.id}`}
                >
                  {liveLabel} · ₹{formatMoney(liveRate)}/hr
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider text-zinc-600" data-testid={`live-rate-label-${table.id}`}>
                  ₹{formatMoney(liveRate)}/hr
                </span>
              )}
            </div>
            )}

            {/* Loser pays: tap whoever lost the frame. Each player's own
                frames and their own drinks add up on their line. */}
            {framesGame && (
              <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 space-y-2" data-testid={`frames-${table.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs text-zinc-400">
                    <Users className="w-3.5 h-3.5" /> Loser pays · by table time
                  </span>
                  <span className="text-xs text-zinc-500">
                    <button
                      type="button"
                      data-testid={`toggle-frames-${table.id}`}
                      onClick={() => setShowFrames((v) => !v)}
                      disabled={tally.frameCount === 0}
                      className="underline decoration-dotted underline-offset-2 hover:text-zinc-200 disabled:no-underline disabled:hover:text-zinc-500 transition-colors duration-150"
                    >
                      {tally.frameCount} frame{tally.frameCount === 1 ? "" : "s"}
                    </button>
                    {tally.frameCount > 0 && (
                      <button
                        type="button"
                        data-testid={`undo-frame-${table.id}`}
                        onClick={() => handleRemoveFrame((session.frames || [])[(session.frames || []).length - 1])}
                        disabled={frameBusy}
                        className="ml-2 text-zinc-500 hover:text-red-400 disabled:opacity-40 transition-colors duration-150"
                      >
                        undo last
                      </button>
                    )}
                  </span>
                </div>

                {/* Every frame played: when it ran, for how long, what it cost */}
                {showFrames && tally.frameCount > 0 && (
                  <div className="rounded-md bg-zinc-900/60 divide-y divide-zinc-800/70" data-testid={`frame-log-${table.id}`}>
                    {(session.frames || []).map((f, i) => (
                      <div key={f.id || i} className="flex items-center gap-2 px-2 py-1.5 text-[11px]">
                        <span className="text-zinc-600 shrink-0 w-4">{i + 1}</span>
                        <span className="flex-1 min-w-0 truncate text-zinc-300">{f.loser_name}</span>
                        <span className="text-zinc-500 shrink-0">
                          {formatTime(f.from)}–{formatTime(f.at)}
                        </span>
                        <span className="text-zinc-400 shrink-0 w-12 text-right">
                          {formatDuration(f.minutes)}
                        </span>
                        <span className="text-zinc-200 shrink-0 w-10 text-right">₹{formatMoney(f.amount)}</span>
                        <button
                          type="button"
                          aria-label={`Remove frame ${i + 1}`}
                          data-testid={`remove-frame-${f.id}`}
                          onClick={() => handleRemoveFrame(f)}
                          disabled={frameBusy}
                          className="shrink-0 w-5 h-5 rounded flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40 transition-colors duration-150"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* What the frame being played has run up so far */}
                <div className="flex items-center justify-between text-xs rounded-md bg-zinc-900/60 px-2 py-1.5" data-testid={`this-frame-${table.id}`}>
                  <span className="text-zinc-400">
                    This frame{isPaused ? " (paused)" : ""} · {formatDuration(thisFrame.minutes)}
                  </span>
                  <span className="text-zinc-100 font-medium">₹{formatMoney(thisFrame.amount)}</span>
                </div>

                <div className="space-y-1.5">
                  {tally.sides.map((side) => (
                    <div key={side.sideId} className="flex items-center gap-2 text-sm" data-testid={`side-row-${table.id}-${side.sideId}`}>
                      {/* The name keeps its own line: in a narrow card a row of
                          figures would otherwise squeeze it to nothing. */}
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-zinc-200">
                          {side.name}
                          {side.isTeam && (
                            <span className="text-zinc-500 text-xs ml-1.5">
                              {side.members.map((m) => m.name).join(" + ")}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {side.frames} frame{side.frames === 1 ? "" : "s"} ₹{formatMoney(side.framesAmount)}
                          {side.itemsTotal > 0 && (
                            <span className="text-amber-400"> + ₹{formatMoney(side.itemsTotal)} items</span>
                          )}
                        </div>
                      </div>
                      <span className="text-zinc-100 font-medium shrink-0 text-right">₹{formatMoney(side.total)}</span>
                      {/* Put a drink or a cigarette straight onto this name */}
                      <Button
                        aria-label={`Add items for ${side.name}`}
                        data-testid={`add-items-for-${table.id}-${side.sideId}`}
                        onClick={() => openItemsFor({ ...side, member_ids: side.members.map((m) => m.playerId) })}
                        className="h-8 w-8 p-0 shrink-0 bg-zinc-800 hover:bg-amber-600 text-zinc-300 hover:text-white rounded-md"
                      >
                        <ShoppingBag className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        data-testid={`frame-lost-${table.id}-${side.sideId}`}
                        onClick={() => handleFrameLost(side)}
                        disabled={frameBusy}
                        className="h-8 px-2.5 shrink-0 text-xs font-heading font-bold bg-zinc-800 hover:bg-red-600 text-zinc-200 hover:text-white rounded-md"
                      >
                        Lost
                      </Button>
                    </div>
                  ))}
                </div>
                {tally.tableItemsTotal > 0 && (
                  <div className="flex items-center justify-between text-xs text-zinc-500 border-t border-zinc-800 pt-1.5">
                    <span>On the table (nobody's name)</span>
                    <span className="text-zinc-300">₹{formatMoney(tally.tableItemsTotal)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Controllers — changeable mid-game. Each stretch is billed at the
                rate for the pads in play at the time. */}
            {isPlaystation && (
              <div
                className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-950/40 px-3 py-2"
                data-testid={`controllers-${table.id}`}
              >
                <span className="flex items-center gap-1.5 text-xs text-zinc-400">
                  <Gamepad2 className="w-3.5 h-3.5" /> Controllers
                </span>
                <div className="flex items-center gap-1">
                  {CONTROLLER_COUNTS.map((count) => {
                    const active = Number(count) === Number(liveControllers);
                    return (
                      <button
                        key={count}
                        type="button"
                        aria-label={controllerLabel(count)}
                        aria-pressed={active}
                        data-testid={`set-controllers-${table.id}-${count}`}
                        onClick={() => handleSetControllers(count)}
                        disabled={switchingControllers}
                        className={`w-8 h-8 rounded-md text-sm font-semibold transition-colors duration-150 disabled:opacity-50 ${
                          active
                            ? "bg-blue-600 text-white"
                            : "bg-[#09090b] border border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
                        }`}
                      >
                        {count}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Each stretch played on this console with a different number of
                pads. Loser pays shows its money frame by frame instead. */}
            {isPlaystation && !framesGame && thisTable.segments.length > 1 && (
              <div className="space-y-0.5 px-1" data-testid={`rate-segments-${table.id}`}>
                {thisTable.segments.map((seg) => (
                  <div key={`${seg.rate}|${seg.label}`} className="flex items-center justify-between text-[11px] text-zinc-500">
                    <span className="min-w-0 pr-2">{seg.label} · ₹{formatMoney(seg.rate)}/hr</span>
                    <span className="shrink-0">
                      {formatDuration(seg.minutes)} · ₹{formatMoney(seg.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Tab — items bought at the table while playing */}
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/40" data-testid={`tab-${table.id}`}>
              {tabLines.length > 0 && (
                <div className="max-h-36 overflow-y-auto divide-y divide-zinc-800/70">
                  {tabLines.map((line) => (
                    <div key={line.itemId} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`tab-line-${table.id}-${line.itemId}`}>
                      <button
                        type="button"
                        aria-label={`Remove one ${line.name}`}
                        onClick={() => handleRemoveOne(line)}
                        disabled={removingId === line.itemId}
                        className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40 transition-colors duration-150"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="flex-1 min-w-0 text-zinc-200 break-words">
                        {line.name}
                        {framesGame ? (
                          <button
                            type="button"
                            data-testid={`move-line-${table.id}-${line.itemId}`}
                            onClick={() => setMovingLine(movingLine?.itemId === line.itemId ? null : line)}
                            className={`text-xs ml-1.5 underline decoration-dotted underline-offset-2 transition-colors duration-150 ${
                              line.players.size > 0 ? "text-zinc-500 hover:text-amber-400" : "text-amber-400/80 hover:text-amber-300"
                            }`}
                          >
                            {line.players.size > 0 ? [...line.players].join(", ") : "nobody's"}
                          </button>
                        ) : (
                          line.players.size > 0 && (
                            <span className="text-zinc-500 text-xs ml-1.5">{[...line.players].join(", ")}</span>
                          )
                        )}
                      </span>
                      <span className="text-zinc-500 text-xs shrink-0">× {line.quantity}</span>
                      <span className="text-zinc-100 font-medium shrink-0 w-14 text-right">₹{formatMoney(line.amount)}</span>
                    </div>
                  ))}
                  {framesGame && movingLine && (
                    <div className="px-3 py-2 bg-zinc-900/60" data-testid={`move-picker-${table.id}`}>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Move {movingLine.name} to
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {[...(session?.players || []), { id: "table", name: "The table" }].map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`move-to-${p.id}`}
                            onClick={() => handleMoveLine(movingLine, p.id)}
                            className="h-7 px-2.5 rounded-md text-xs font-medium bg-[#09090b] border border-zinc-700 text-zinc-300 hover:border-amber-500/60 hover:text-amber-300 transition-colors duration-150"
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                data-testid={`add-items-btn-${table.id}`}
                onClick={() => openItemsFor(null)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm hover:bg-zinc-800/50 transition-colors duration-150 ${
                  tabLines.length > 0 ? "border-t border-zinc-800 rounded-b-lg" : "rounded-lg"
                }`}
              >
                <span className="flex items-center gap-2 text-zinc-300 font-medium">
                  {tabLines.length > 0 ? <Plus className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
                  {tabLines.length > 0 ? "Add more" : "Add cigarettes, drinks, food"}
                </span>
                {tabTotal > 0 && (
                  <span className="text-zinc-400 text-xs">
                    Items <span className="text-zinc-100 font-semibold text-sm" data-testid={`tab-total-${table.id}`}>₹{formatMoney(tabTotal)}</span>
                  </span>
                )}
              </button>
            </div>

            {/* Pause / Resume beside Stop */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                data-testid={`pause-btn-${table.id}`}
                onClick={handleTogglePause}
                disabled={pauseLoading}
                className={`h-14 text-lg font-heading font-bold tracking-wide rounded-lg transition-colors duration-200 ${
                  isPaused
                    ? (isSnooker ? "bg-green-600 hover:bg-green-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white")
                    : "bg-amber-500 hover:bg-amber-600 text-zinc-950"
                }`}
                size="lg"
              >
                {isPaused ? <Play className="w-5 h-5 mr-2" /> : <Pause className="w-5 h-5 mr-2" />}
                {pauseLoading ? "..." : isPaused ? "Resume" : "Pause"}
              </Button>
              <Button
                data-testid={`stop-btn-${table.id}`}
                onClick={openStopConfirm}
                className="h-14 text-lg font-heading font-bold tracking-wide bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
                size="lg"
              >
                <Square className="w-5 h-5 mr-2" />
                Stop
              </Button>
            </div>

            <button
              type="button"
              data-testid={`move-btn-${table.id}`}
              onClick={openMove}
              className="w-full h-9 flex items-center justify-center gap-1.5 rounded-lg border border-zinc-800 text-xs font-medium text-zinc-400 hover:text-zinc-100 hover:border-zinc-600 transition-colors duration-150"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
              Move to another table
            </button>

            {/* Started by mistake. Kept small, in its own row and behind a
                confirmation — never beside Stop, where a stray tap would land. */}
            <button
              type="button"
              data-testid={`cancel-btn-${table.id}`}
              onClick={() => setShowCancel(true)}
              className="w-full text-center text-xs text-zinc-600 hover:text-red-400 transition-colors duration-150 py-0.5"
            >
              Started by mistake? Discard this game
            </button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="text-center py-6">
              <p className="text-zinc-500 text-sm uppercase tracking-wider font-medium">
                Available
              </p>
              {table.table_type === "playstation" ? (
                <div className="mt-3 space-y-3">
                  <div className="text-left text-xs uppercase tracking-[0.24em] text-zinc-500">
                    Controller count
                  </div>
                  <Select
                    value={String(controllerCount)}
                    onValueChange={(value) => setControllerCount(Number(value))}
                  >
                    <SelectTrigger className="w-full bg-[#09090b] border-zinc-700 text-zinc-100">
                      <SelectValue placeholder="Select controllers" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTROLLER_COUNTS.map((count) => (
                        <SelectItem key={count} value={count}>{controllerLabel(count)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="rounded-xl border border-zinc-700 bg-[#0f172a] p-3">
                    <div className="text-zinc-400 text-xs uppercase tracking-[0.2em] mb-1">
                      Hourly rate
                    </div>
                    {activeWindowNow ? (
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-semibold text-amber-400">₹{formatMoney(activeWindowNow.rate)}/hr</span>
                        <span className="text-xs text-zinc-500 line-through">₹{formatMoney(tableRate)}</span>
                      </div>
                    ) : (
                      <div className="text-lg font-semibold text-zinc-100">₹{formatMoney(tableRate)}/hr</div>
                    )}
                  </div>
                </div>
              ) : isSnooker ? (
                /* Loser pays — each frame is charged to whoever lost it,
                   instead of charging the table by the hour. */
                <div className="mt-3 space-y-3 text-left" data-testid={`loser-pays-setup-${table.id}`}>
                  <button
                    type="button"
                    data-testid={`loser-pays-toggle-${table.id}`}
                    onClick={() => setLoserPays((v) => !v)}
                    className={`w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors duration-150 ${
                      loserPays
                        ? "border-green-600/60 bg-green-600/10 text-green-300"
                        : "border-zinc-700 bg-[#09090b] text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    <span className="flex items-center gap-2 font-medium">
                      <Trophy className="w-4 h-4" /> Loser pays (per frame)
                    </span>
                    <span className="text-xs">{loserPays ? "ON" : "OFF"}</span>
                  </button>

                  {loserPays ? (
                    <>
                      {/* Singles or 2 v 2 — a frame is charged to a side */}
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { id: "singles", label: "Singles", on: !teamPlay },
                          { id: "teams", label: "Teams", on: teamPlay },
                        ].map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            data-testid={`play-mode-${m.id}-${table.id}`}
                            onClick={() => setTeamPlay(m.id === "teams")}
                            className={`h-9 rounded-lg text-sm font-medium transition-colors duration-150 ${
                              m.on
                                ? "bg-green-600 text-white"
                                : "bg-[#09090b] border border-zinc-700 text-zinc-400 hover:text-zinc-200"
                            }`}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>

                      {teamPlay ? (
                        <div className="space-y-3">
                          {teamNames.map((names, teamIndex) => (
                            <div key={teamIndex} className="rounded-lg border border-zinc-800 p-2 space-y-2">
                              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                                Team {String.fromCharCode(65 + teamIndex)}
                              </div>
                              {names.map((name, i) => (
                                <Input
                                  key={i}
                                  data-testid={`team-${teamIndex}-player-${i}-${table.id}`}
                                  placeholder={`Player ${i + 1}`}
                                  value={name}
                                  maxLength={30}
                                  onChange={(e) => setTeamNames((prev) => prev.map((team, t) =>
                                    t === teamIndex ? team.map((n, j) => (j === i ? e.target.value : n)) : team))}
                                  className="h-9 bg-[#09090b] border-zinc-700 text-zinc-100"
                                />
                              ))}
                              {names.length < 4 && (
                                <button
                                  type="button"
                                  onClick={() => setTeamNames((prev) => prev.map((team, t) =>
                                    t === teamIndex ? [...team, ""] : team))}
                                  className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-green-400 transition-colors duration-150"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add to this team
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {playerNames.map((name, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Input
                                data-testid={`player-name-${table.id}-${i}`}
                                placeholder={`Player ${i + 1}`}
                                value={name}
                                maxLength={30}
                                onChange={(e) => setPlayerNames((prev) =>
                                  prev.map((n, j) => (j === i ? e.target.value : n)))}
                                className="h-10 bg-[#09090b] border-zinc-700 text-zinc-100"
                              />
                              {playerNames.length > 2 && (
                                <button
                                  type="button"
                                  aria-label={`Remove player ${i + 1}`}
                                  onClick={() => setPlayerNames((prev) => prev.filter((_, j) => j !== i))}
                                  className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          {playerNames.length < 8 && (
                            <button
                              type="button"
                              data-testid={`add-player-${table.id}`}
                              onClick={() => setPlayerNames((prev) => [...prev, ""])}
                              className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-green-400 transition-colors duration-150"
                            >
                              <Plus className="w-3.5 h-3.5" /> Add player
                            </button>
                          )}
                        </div>
                      )}
                      <p className="text-[11px] text-zinc-500">
                        Each frame costs the table time it took, at ₹{formatMoney(tableRate)}/hr, and
                        goes on whoever lost it.
                      </p>
                    </>
                  ) : activeWindowNow ? (
                    <p className="text-xs">
                      <span className="text-amber-400 font-semibold">₹{formatMoney(activeWindowNow.rate)}/hr</span>
                      <span className="text-zinc-600 line-through ml-1.5">₹{formatMoney(tableRate)}</span>
                    </p>
                  ) : (
                    <p className="text-zinc-600 text-xs">₹{formatMoney(tableRate)}/hr</p>
                  )}
                </div>
              ) : activeWindowNow ? (
                <p className="text-xs mt-1">
                  <span className="text-amber-400 font-semibold">₹{formatMoney(activeWindowNow.rate)}/hr</span>
                  <span className="text-zinc-600 line-through ml-1.5">₹{formatMoney(tableRate)}</span>
                </p>
              ) : (
                <p className="text-zinc-600 text-xs mt-1">
                  ₹{formatMoney(tableRate)}/hr
                </p>
              )}
              {activeWindowNow && (
                <span
                  className="inline-block mt-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400"
                  data-testid={`offer-badge-${table.id}`}
                >
                  {activeWindowNow.label} active
                </span>
              )}
            </div>
            <Button
              data-testid={`start-btn-${table.id}`}
              onClick={handleStart}
              disabled={actionLoading}
              className={`w-full h-14 text-lg font-heading font-bold tracking-wide rounded-lg transition-colors duration-200 ${
                isSnooker
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
              size="lg"
            >
              <Play className="w-5 h-5 mr-2" />
              {actionLoading ? "Starting..." : "Start Game"}
            </Button>
          </div>
        )}
      </div>

      {/* Stop confirmation — the game keeps running until Confirm Payment.
          Cancel / ESC / backdrop all keep the game going (nothing is stopped). */}
      <Dialog open={showBill} onOpenChange={(open) => { if (!open) handleCancelStop(); }}>
        <DialogContent className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto" data-testid="bill-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Stop {table.name}?</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Confirm the payment to end the session, or cancel to keep playing.
            </DialogDescription>
          </DialogHeader>
          {session && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="text-zinc-400">Started</div>
                <div className="text-right font-medium">{formatTime(session.start_time)}</div>
                <div className="text-zinc-400">Played</div>
                <div className="text-right font-medium font-mono-timer">
                  {formatDuration(billedMinutes)}
                </div>
                {pausedMinutes >= 1 && (
                  <>
                    <div className="text-zinc-400">Paused (not charged)</div>
                    <div className="text-right font-medium font-mono-timer text-amber-400">
                      {formatDuration(pausedMinutes)}
                    </div>
                  </>
                )}
                {(framesGame || moves.length === 0) && (
                  <>
                    <div className="text-zinc-400">{framesGame ? "Frames" : "Rate"}</div>
                    <div className="text-right font-medium">
                      {framesGame
                        ? `${tally.frameCount} played · ₹${formatMoney(tally.framesTotal)}`
                        : <>₹{formatMoney(liveRate)}/hr {isOfferRateNow && <span className="text-amber-400">({liveLabel})</span>}</>}
                    </div>
                  </>
                )}
              </div>

              {/* Moved between tables: each table's time, rate and items */}
              {moves.length > 0 && <TableStretches stretches={stretches} orders={orders} running />}

              {/* Who settles what — each player's own frames and own items */}
              {framesGame && (
                <div className="rounded-lg border border-zinc-700 bg-zinc-950/60 p-3 space-y-1.5" data-testid="player-split">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Who pays what</div>
                  {(session.frames || []).length > 0 && (
                    <div className="mb-2 pb-2 border-b border-zinc-800 space-y-1" data-testid="bill-frame-log">
                      {(session.frames || []).map((f, i) => (
                        <div key={f.id || i} className="flex items-center gap-2 text-[11px]">
                          <span className="text-zinc-600 shrink-0 w-4">{i + 1}</span>
                          <span className="flex-1 min-w-0 truncate text-zinc-400">{f.loser_name}</span>
                          <span className="text-zinc-500 shrink-0">{formatTime(f.from)}–{formatTime(f.at)}</span>
                          <span className="text-zinc-400 shrink-0 w-14 text-right">
                            {formatDuration(f.minutes)}
                          </span>
                          <span className="text-zinc-300 shrink-0 w-12 text-right">₹{formatMoney(f.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {tally.sides.map((side) => (
                    <div key={side.sideId} className="flex items-center justify-between text-xs" data-testid={`bill-side-${side.sideId}`}>
                      <span className="text-zinc-400 truncate pr-2">
                        {side.name}
                        {side.isTeam && ` (${side.members.map((m) => m.name).join(" + ")})`}
                        {" · "}{side.frames} frame{side.frames === 1 ? "" : "s"} ₹{formatMoney(side.framesAmount)}
                        {side.itemsTotal > 0 && ` + ₹${formatMoney(side.itemsTotal)} items`}
                      </span>
                      <span className="text-zinc-200 font-medium shrink-0">₹{formatMoney(side.total)}</span>
                    </div>
                  ))}
                  {(thisFrame.amount > 0 || tally.tableItemsTotal > 0) && (
                    <div className="border-t border-zinc-800 pt-1.5 space-y-1">
                      {thisFrame.amount > 0 && (
                        <div className="flex items-center justify-between text-xs" data-testid="bill-unfinished-frame">
                          <span className="text-zinc-500">
                            Frame in progress · {formatDuration(thisFrame.minutes)} (nobody lost it)
                          </span>
                          <span className="text-zinc-300">₹{formatMoney(thisFrame.amount)}</span>
                        </div>
                      )}
                      {tally.tableItemsTotal > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-zinc-500">Items on the table</span>
                          <span className="text-zinc-300">₹{formatMoney(tally.tableItemsTotal)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Rate breakdown when the session spanned more than one pricing window */}
              {!framesGame && moves.length === 0 && liveSegments.length > 1 && (
                <div className="rounded-lg border border-zinc-700 bg-zinc-950/60 p-3 space-y-1.5" data-testid="rate-breakdown">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Rate breakdown</div>
                  {liveSegments.map((s) => (
                    <div key={`${s.rate}|${s.label}`} className="flex items-center justify-between text-xs">
                      {/* "Normal · 3 Controllers" is still the normal rate */}
                      <span className={s.label.startsWith("Normal") ? "text-zinc-400" : "text-amber-400"}>
                        {s.label} · {formatDuration(s.minutes)} @ ₹{formatMoney(s.rate)}/hr
                      </span>
                      <span className="text-zinc-200 font-medium">₹{formatMoney(Math.round(s.amount))}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Game + tab, itemised so the customer can check the bill */}
              {moves.length === 0 && tabLines.length > 0 && (
                <div className="rounded-lg border border-zinc-700 bg-zinc-950/60 p-3 space-y-1.5" data-testid="bill-breakdown">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Game</span>
                    <span className="text-zinc-200 font-medium" data-testid="bill-game-amount">₹{formatMoney(gameAmount)}</span>
                  </div>
                  {tabLines.map((line) => (
                    <div key={line.itemId} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 truncate pr-2">{line.name} × {line.quantity}</span>
                      <span className="text-zinc-200 font-medium shrink-0">₹{formatMoney(line.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t border-zinc-700 pt-4 flex items-center justify-between">
                <span className="text-lg text-zinc-300 font-heading font-semibold">Amount Due</span>
                <span className="text-3xl font-heading font-bold text-green-400" data-testid="bill-total-amount">
                  ₹{formatMoney(amountDue)}
                </span>
              </div>

              <BillPayment shares={shares} choices={payChoices} onChange={setPayChoices} />
              {paymentBlocked && (
                <p className="text-xs text-zinc-400" data-testid="payment-blocked">{paymentBlocked}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <div className="grid grid-cols-2 gap-3 w-full">
              <Button
                data-testid={`cancel-stop-btn-${table.id}`}
                onClick={handleCancelStop}
                disabled={actionLoading}
                variant="outline"
                className="h-12 text-lg font-heading font-bold border-zinc-600 bg-transparent text-zinc-200 hover:bg-zinc-800 hover:text-white rounded-lg"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-payment-btn"
                onClick={handleConfirmStop}
                disabled={actionLoading || !!paymentBlocked}
                className="h-12 text-lg font-heading font-bold bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                {actionLoading ? "Recording..." : payingLater ? "Confirm Dues" : "Confirm Payment"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isOccupied && session && (
        <AddToTabDialog
          open={showAddItems}
          onOpenChange={(next) => { setShowAddItems(next); if (!next) setAddItemsFor(null); }}
          table={table}
          forSide={addItemsFor}
        />
      )}

      {/* Pick where the game goes. Tapping a table is the confirmation: a move
          costs nothing by itself, and it can be moved straight back. */}
      <Dialog open={showMove} onOpenChange={(open) => { if (!movingTo) setShowMove(open); }}>
        <DialogContent className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-md" data-testid={`move-dialog-${table.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Move this game</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {table.name}'s time and items stay on the bill, to be paid once at the end.
              The new table starts fresh at its own rate.
            </DialogDescription>
          </DialogHeader>
          {moveTargets.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {moveTargets.map((t) => {
                // A console game brings its pads; anyone else takes what's set there.
                const pads = isPlaystation ? liveControllers : t.controller_count ?? 2;
                const offer = offerNow(t, settings, pads);
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`move-to-table-${t.id}`}
                    onClick={() => handleMove(t)}
                    disabled={!!movingTo}
                    className="w-full flex items-center justify-between gap-3 rounded-lg border border-zinc-700 bg-[#09090b] px-3 py-2.5 text-left hover:border-green-600/60 disabled:opacity-50 transition-colors duration-150"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-zinc-100">{t.name}</span>
                      <span className="block text-xs text-zinc-500">
                        {TABLE_TYPE_LABELS[t.table_type] || t.table_type}
                        {t.table_type === "playstation" && ` · ${controllerLabel(pads)}`}
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-sm text-zinc-200">
                      {movingTo === t.id ? "Moving..." : `₹${formatMoney(offer?.rate ?? hourlyRate(t, settings, pads))}/hr`}
                      {offer && <span className="block text-[10px] text-amber-400">{offer.label}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-zinc-500" data-testid={`move-none-${table.id}`}>
              No other table is free right now.
            </p>
          )}
        </DialogContent>
      </Dialog>

      <WaitingPickDialog
        tabs={waitingChoices}
        table={table}
        onPick={startGame}
        onSkip={() => startGame()}
        onClose={() => setWaitingChoices([])}
      />

      {/* Discard confirmation — says plainly what is being thrown away */}
      <AlertDialog open={showCancel} onOpenChange={(open) => { if (!cancelLoading) setShowCancel(open); }}>
        <AlertDialogContent className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-md" data-testid={`cancel-dialog-${table.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl tracking-tight">Discard this game?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400 space-y-2">
              <span className="block">
                {table.name} has been running {formatDuration(billedMinutes)} and would bill{" "}
                <span className="text-zinc-100 font-semibold">₹{formatMoney(gameAmount)}</span>. Discarding
                records nothing — it won't appear in history or in the day's takings.
              </span>
              {keptLines.length > 0 && (
                <span className="block text-amber-400/90">
                  The {keptLines.length} item{keptLines.length > 1 ? "s" : ""} on the tab
                  (₹{formatMoney(keptTotal)}) stay recorded as cafe sales.
                </span>
              )}
              {waitingNames && (
                <span className="block text-amber-400/90" data-testid={`discard-waiting-${table.id}`}>
                  What {waitingNames} bought while waiting goes back on the waiting list.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
              data-testid={`keep-playing-btn-${table.id}`}
            >
              Keep playing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCancelGame(); }}
              disabled={cancelLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`confirm-cancel-btn-${table.id}`}
            >
              {cancelLoading ? "Discarding..." : "Discard game"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
